import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HP2, SERVICE_ROUTES } from './assets';

const HASH_TO_SLIDE = { '#deep-reset': 1, '#commercial-cleaning': 2, '#now-hiring': 3 };
const TOTAL = 4;
const DOT_LABELS = ['Slide 1', 'Slide 2 — Deep Reset', 'Slide 3 — Commercial Cleaning', 'Slide 4 — Now Hiring'];

const Cta = ({ to, className = '', children, testId }) => (
  <Link className={`cta ${className}`} to={to} data-testid={testId}>
    <b>{children}</b><span aria-hidden="true">↗</span>
  </Link>
);

export const HeroCampaigns = () => {
  const location = useLocation();
  const [index, setIndex] = useState(() => HASH_TO_SLIDE[location.hash] ?? 0);

  useEffect(() => {
    const target = HASH_TO_SLIDE[location.hash];
    if (target !== undefined) setIndex(target);
  }, [location.hash]);

  const show = useCallback((i) => setIndex((i + TOTAL) % TOTAL), []);

  const heroClass = [
    'hero',
    index > 0 && 'reset-active',
    index === 2 && 'commercial-active',
    index === 3 && 'hiring-active',
  ].filter(Boolean).join(' ');

  const slide = (i, name) => ({
    hidden: index !== i,
    className: `hero-slide ${name} ${index === i ? 'entering' : ''}`,
  });

  return (
    <section className={heroClass} aria-label="Homepage campaigns" id="campaigns" data-testid="hero-campaigns">
      <div {...slide(0, 'brand-slide')} data-testid="hero-slide-brand">
        <div className="hero-copy">
          <p className="positioning">PORTLAND &amp; VANCOUVER'S PREMIUM CLEANING SERVICE</p>
          <div className="wordmark hero-logo"><img src={HP2.logo} alt="Gleam Guru Cleaners" /></div>
          <p className="tagline">THE MASTERS OF CLEAN.</p>
          <Cta to="/#quote-form" testId="hero-brand-cta">TALK WITH A GLEAM GURU</Cta>
        </div>
        <div className="guru"><img src={HP2.guruHead} alt="Official color Guru Head" /></div>
      </div>

      <div {...slide(1, 'reset-slide')} data-testid="hero-slide-deep-reset">
        <img className="reset-photo" src={HP2.deepResetPhoto} alt="Wide luxury loft living room with panoramic windows" />
        <div className="reset-shade" />
        <div className="reset-copy">
          <p className="reset-eyebrow">DEEP RESET CLEAN</p>
          <h1>DEEP<br /><span>RESET.</span></h1>
          <p className="reset-intro">Our recommended starting point<br />for most new clients.</p>
          <Cta to={SERVICE_ROUTES.deep_reset} className="reset-cta" testId="hero-deep-reset-cta">EXPLORE DEEP RESET</Cta>
        </div>
      </div>

      <div {...slide(2, 'commercial-slide')} data-testid="hero-slide-commercial">
        <div className="commercial-art"><img src={HP2.commercialArt} alt="Commercial campaign image adapted from the supplied flyer" /></div>
        <div className="commercial-copy">
          <p className="commercial-eyebrow">COMMERCIAL CLEANING</p>
          <h1 className="commercial-book-title">
            <small>BOOK OUR</small><b>COMMERCIAL</b><span>CLEANING</span><em>SERVICES.</em>
          </h1>
          <p className="commercial-slogan">The masters of office cleaning.</p>
          <Cta to={SERVICE_ROUTES.commercial} className="commercial-cta" testId="hero-commercial-cta">EXPLORE COMMERCIAL CLEANING</Cta>
        </div>
      </div>

      <div {...slide(3, 'hiring-slide')} data-testid="hero-slide-hiring">
        <div className="hiring-art"><img src={HP2.hiringArt} alt="Wide hiring campaign image adapted from the supplied flyer" /></div>
        <div className="hiring-copy">
          <p className="hiring-location">PORTLAND, OR · VANCOUVER, WA</p>
          <h1 className="hiring-original-title" aria-label="Now Hiring"><img src={HP2.hiringTitle} alt="Now Hiring" /></h1>
          <p className="hiring-role">WE ARE LOOKING FOR EXPERIENCED HOUSE &amp; COMMERCIAL CLEANERS. CLICK BELOW TO APPLY!</p>
          <Cta to="/careers" className="hiring-cta" testId="hero-hiring-cta">APPLY NOW</Cta>
        </div>
      </div>

      <div className="slide-nav">
        <span className="count" data-testid="hero-slide-count">{`0${index + 1} / 04`}</span>
        <div className="dots" aria-label="Campaign slide navigation">
          {DOT_LABELS.map((label, i) => (
            <button
              key={label}
              type="button"
              className={`dot ${index === i ? 'active' : ''}`}
              aria-label={label}
              aria-current={index === i ? 'true' : 'false'}
              onClick={() => show(i)}
              data-testid={`hero-dot-${i + 1}`}
            />
          ))}
        </div>
        <div className="arrows">
          <button type="button" className="arrow" aria-label="Previous slide" onClick={() => show(index - 1)} data-testid="hero-prev">←</button>
          <button type="button" className="arrow" aria-label="Next slide" onClick={() => show(index + 1)} data-testid="hero-next">→</button>
        </div>
      </div>
    </section>
  );
};
